<template>
  <div
    v-if="currentVariant !== 'control'"
    class="pdp-discount-incentive"
    :class="{ 'pdp-discount-incentive--unlocked': activeStepIndex >= 1 }"
  >
    <!-- Header Row -->
    <div class="pdp-discount-incentive__header" style="margin-left: 10px;">
      <div class="pdp-discount-incentive__header-left">
        <!-- Gift Icon -->
        <svg
          class="pdp-discount-incentive__gift-icon"
          viewBox="0 0 32 32"
          width="24"
          height="24"
        >
          <rect x="5" y="14" width="22" height="14" rx="2" fill="#FBBF24" />
          <rect x="3" y="10" width="26" height="5" rx="1.5" fill="#F59E0B" />
          <rect x="13" y="10" width="6" height="18" fill="#EF4444" />
          <rect x="3" y="11.5" width="26" height="2" fill="#DC2626" />
          <path
            d="M12 10C12 10 7 8 7 5.5C7 3.5 9 2.5 11 4C12.5 5.1 13.5 7.5 14 10"
            fill="#EF4444"
          />
          <path
            d="M20 10C20 10 25 8 25 5.5C25 3.5 23 2.5 21 4C19.5 5.1 18.5 7.5 18 10"
            fill="#EF4444"
          />
        </svg>
        <span class="pdp-discount-incentive__header-title"
          >Buy more, save more</span
        >
      </div>
    </div>

    <!-- Stepper / Progress Bar -->
    <div class="pdp-discount-incentive__stepper">
      <div class="pdp-discount-incentive__stepper-track">
        <div
          class="pdp-discount-incentive__stepper-bar"
          :style="{ width: `${progressPercentage}%` }"
        ></div>
      </div>
      <div class="pdp-discount-incentive__stepper-steps">
        <div
          v-for="(step, index) in stepperTiers"
          :key="index"
          class="pdp-discount-incentive__stepper-step"
          :class="{
            'pdp-discount-incentive__stepper-step--active':
              index <= activeStepIndex,
            'pdp-discount-incentive__stepper-step--current':
              index === activeStepIndex,
            'pdp-discount-incentive__stepper-step--just-unlocked':
              index === justUnlockedStepIndex,
            [`pdp-discount-incentive__stepper-step--just-unlocked-step-${index}`]:
              index === justUnlockedStepIndex,
          }"
        >
          <div class="pdp-discount-incentive__stepper-node">
            <svg
              v-if="index <= activeStepIndex"
              class="pdp-discount-incentive__stepper-check"
              viewBox="0 0 12 12"
              fill="none"
            >
              <path
                d="M2.5 6.5L4.5 8.5L9.5 3.5"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="pdp-discount-incentive__stepper-label">
            <span class="pdp-discount-incentive__stepper-count">{{
              step.count
            }}</span>
            <span
              v-if="step.discount"
              class="pdp-discount-incentive__stepper-discount"
              >{{ step.discount }}</span
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Fireworks / Confetti Canvas -->
    <canvas
      ref="fireworksCanvas"
      class="pdp-discount-incentive__fireworks-canvas"
    ></canvas>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import useABTestStoreV2 from '@/composables/abtest/store'
import useProductStore from '@/store/product'
import useCartStore from '@/store/cart'
import EventBus from '@/services/eventbus'
import { EVENT_VALIDATE_ATC } from '@/utils'

import Analytics from '@/services/analytics'

const route = useRoute()
const abtestStore = useABTestStoreV2()
const productStore = useProductStore()
const cartStore = useCartStore()

const EVENT_PDP_BUY_MORE_SAVE_MORE = 'PDP Buy More Save More Incentive Action'

function trackIncentiveEvent(
  action: string,
  payload: Record<string, any> = {}
) {
  try {
    Analytics.track(EVENT_PDP_BUY_MORE_SAVE_MORE, {
      action,
      product: productStore.product?.handle,
      variant: currentVariant.value,
      cart_items_count: cartStore.totalCartItems || 0,
      ...payload,
    })
  } catch (error: any) {
    try {
      Analytics.log('pdp_buy_more_save_more_track_error', {
        action,
        message: error?.message || error,
      })
    } catch (sentryErr) {}
  }
}

const currentVariant = computed<string>(() => {
  const queryVariant = route.query.pdp_discount_variant as string
  if (queryVariant) {
    return queryVariant.toLowerCase()
  }

  return abtestStore.variant?.toLowerCase() || 'control'
})

const stepperTiers = [
  { count: '1 item', discount: '' },
  { count: '2 items', discount: '5% OFF' },
  { count: '3 items', discount: '10% OFF' },
  { count: '4+ items', discount: '15% OFF' },
]

const pendingAddedCount = ref(0)

function handleValidateAtc(payload?: { quantity?: number }) {
  const added = payload?.quantity ?? 1
  pendingAddedCount.value += added
}

onMounted(() => {
  EventBus.on(EVENT_VALIDATE_ATC, handleValidateAtc)
})


watch(
  () => cartStore.totalCartItems,
  () => {
    pendingAddedCount.value = 0
  }
)

const activeStepIndex = computed<number>(() => {
  const count = (cartStore.totalCartItems || 0) + pendingAddedCount.value
  if (count <= 0) return -1
  if (count === 1) return 0
  if (count === 2) return 1
  if (count === 3) return 2
  return 3
})

const progressPercentage = computed<number>(() => {
  const idx = activeStepIndex.value
  if (idx < 0) return 0
  return (idx / 3) * 100
})

const justUnlockedStepIndex = ref<number>(-1)
let unlockTimeout: any = null

const fireworksCanvas = ref<HTMLCanvasElement | null>(null)
let fireworksAnimationId: number | null = null

interface FireworkParticle {
  x: number
  y: number
  vx: number
  vy: number
  size: number
  color: string
  alpha: number
  decay: number
  gravity: number
  shape: 'circle' | 'rect' | 'star'
  rotation: number
  rotationSpeed: number
}

const FIREWORK_COLORS = [
  '#F59E0B',
  '#EF4444',
  '#10B981',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#FBBF24',
  '#34D399',
]

function triggerFireworks(targetStep: number) {
  try {
    const canvas = fireworksCanvas.value
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width
    canvas.height = rect.height

    if (fireworksAnimationId) {
      cancelAnimationFrame(fireworksAnimationId)
      fireworksAnimationId = null
    }

    // Xác định tâm nổ dựa trên vị trí chính xác của node step vừa unlock
    const stepNodes = document.querySelectorAll(
      '.pdp-discount-incentive__stepper-node'
    )
    const targetNode = stepNodes[targetStep] as HTMLElement | undefined

    let originX = canvas.width / 2
    let originY = canvas.height / 2

    if (targetNode) {
      const nodeRect = targetNode.getBoundingClientRect()
      originX = nodeRect.left + nodeRect.width / 2 - rect.left
      originY = nodeRect.top + nodeRect.height / 2 - rect.top
    }

    // Cấu hình pháo hoa tăng dần số lượng & màu sắc nhưng giữ nguyên thời lượng như step 1
    const stepConfigs = [
      {
        count: 36,
        speedMin: 2,
        speedMax: 4.5,
        sizeMin: 3,
        sizeMax: 5,
        decayMin: 0.012,
        decayMax: 0.016,
        colors: ['#10B981', '#34D399', '#6EE7B7', '#3B82F6', '#F59E0B'],
      },
      {
        count: 55,
        speedMin: 2.8,
        speedMax: 5.5,
        sizeMin: 3.5,
        sizeMax: 6,
        decayMin: 0.012,
        decayMax: 0.016,
        colors: ['#F59E0B', '#FBBF24', '#10B981', '#EC4899', '#8B5CF6', '#3B82F6'],
      },
      {
        count: 80,
        speedMin: 3.2,
        speedMax: 6.8,
        sizeMin: 4,
        sizeMax: 7,
        decayMin: 0.012,
        decayMax: 0.016,
        colors: ['#EF4444', '#F59E0B', '#FBBF24', '#EC4899', '#8B5CF6', '#10B981', '#3B82F6'],
      },
      {
        count: 110,
        speedMin: 3.8,
        speedMax: 8,
        sizeMin: 4.5,
        sizeMax: 8,
        decayMin: 0.012,
        decayMax: 0.016,
        colors: ['#FFD700', '#FFA500', '#FF1493', '#EF4444', '#8B5CF6', '#10B981', '#00FFFF', '#FF4500'],
      },
    ]

    const config = stepConfigs[Math.min(targetStep, stepConfigs.length - 1)] || stepConfigs[0]
    const particles: FireworkParticle[] = []

    for (let i = 0; i < config.count; i++) {
      const angle = (Math.PI * 2 * i) / config.count + (Math.random() - 0.5) * 0.5
      const speed = Math.random() * (config.speedMax - config.speedMin) + config.speedMin
      const shapes: Array<'circle' | 'rect' | 'star'> = ['circle', 'rect', 'star']
      particles.push({
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - (targetStep >= 2 ? 2.2 : 1.5),
        size: Math.random() * (config.sizeMax - config.sizeMin) + config.sizeMin,
        color: config.colors[Math.floor(Math.random() * config.colors.length)],
        alpha: 1,
        decay: Math.random() * (config.decayMax - config.decayMin) + config.decayMin,
        gravity: targetStep === 3 ? 0.08 : 0.1,
        shape: shapes[Math.floor(Math.random() * shapes.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 14,
      })
    }

    function render() {
      if (!ctx || !canvas) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      let aliveCount = 0

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i]
        p.x += p.vx
        p.y += p.vy
        p.vy += p.gravity
        p.vx *= 0.96
        p.rotation += p.rotationSpeed
        p.alpha -= p.decay

        if (p.alpha > 0) {
          aliveCount++
          ctx.save()
          ctx.globalAlpha = Math.max(0, p.alpha)
          ctx.translate(p.x, p.y)
          ctx.rotate((p.rotation * Math.PI) / 180)
          ctx.fillStyle = p.color

          if (p.shape === 'circle') {
            ctx.beginPath()
            ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2)
            ctx.fill()
          } else if (p.shape === 'rect') {
            ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6)
          } else {
            // Mini star
            ctx.beginPath()
            for (let j = 0; j < 5; j++) {
              ctx.lineTo(
                Math.cos(((18 + j * 72) * Math.PI) / 180) * p.size,
                -Math.sin(((18 + j * 72) * Math.PI) / 180) * p.size
              )
              ctx.lineTo(
                Math.cos(((54 + j * 72) * Math.PI) / 180) * (p.size / 2),
                -Math.sin(((54 + j * 72) * Math.PI) / 180) * (p.size / 2)
              )
            }
            ctx.closePath()
            ctx.fill()
          }

          ctx.restore()
        }
      }

      if (aliveCount > 0) {
        fireworksAnimationId = requestAnimationFrame(render)
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        fireworksAnimationId = null
      }
    }

    render()
  } catch (err) {}
}

onUnmounted(() => {
  EventBus.off(EVENT_VALIDATE_ATC, handleValidateAtc)
  if (fireworksAnimationId) {
    cancelAnimationFrame(fireworksAnimationId)
    fireworksAnimationId = null
  }
  if (unlockTimeout) {
    clearTimeout(unlockTimeout)
    unlockTimeout = null
  }
})

watch(
  () => activeStepIndex.value,
  (newStep, oldStep) => {
    if (newStep > (oldStep ?? -1)) {
      justUnlockedStepIndex.value = newStep
      triggerFireworks(newStep)
      clearTimeout(unlockTimeout)
      unlockTimeout = setTimeout(() => {
        justUnlockedStepIndex.value = -1
      }, 2300)
    }

    if (currentVariant.value !== 'control' && newStep !== oldStep) {
      trackIncentiveEvent('step_change', {
        previous_step: oldStep,
        current_step: newStep,
      })
    }
  }
)
</script>

<style lang="scss">
.pdp-discount-incentive {
  position: relative;
  margin-top: 18px;
  font-family: inherit;
  width: 0;
  min-width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: visible;

  &__fireworks-canvas {
    position: absolute;
    top: -60px;
    bottom: -40px;
    left: -40px;
    right: -40px;
    width: 100%;
    height: calc(100% + 100px);
    pointer-events: none;
    z-index: 100;
  }
  background-color: #fff5f5;
  border: 1px solid #fee2e2;
  border-radius: 12px;
  padding: 12px 14px;
  transition: all 0.3s ease;

  &--unlocked {
    background-color: var(--background-color-primary, #eef8f3);
    border-color: #dcfce7;
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  .highlight-red {
    color: var(--color-secondary, #eb001b);
    font-weight: 700;
  }

  .highlight-green {
    color: var(--color-primary, #38ba72);
    font-weight: 700;
  }

  /* Header row */
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: default;
    width: 100%;
    max-width: 100%;

    &.pdp-discount-incentive__clickable {
      cursor: pointer;
    }
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__gift-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  &__header-title {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
  }

  &__arrow-icon {
    width: 16px;
    height: 16px;
    color: var(--text-color-tertiary, #94a3b8);
    flex-shrink: 0;
    margin-left: 8px;
    transition: transform 0.3s;

    &--expanded {
      transform: rotate(180deg);
    }
  }

  /* Stepper / Progress Bar */
  &__stepper {
    position: relative;
    margin-top: 14px;
    padding: 0 10px;

    &-track {
      position: absolute;
      top: 8px;
      left: 30px;
      right: 30px;
      height: 3px;
      background-color: #e5e7eb;
      border-radius: 2px;
      z-index: 1;
    }

    &-bar {
      height: 100%;
      background: linear-gradient(90deg, #10b981 0%, #059669 100%);
      border-radius: 2px;
      transition: width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
      position: relative;
      overflow: hidden;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255, 255, 255, 0.6),
          transparent
        );
        animation: pdp-shimmer 1.8s infinite;
      }
    }

    &-steps {
      position: relative;
      display: flex;
      justify-content: space-between;
      z-index: 2;
    }

    &-step {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      min-width: 0;
      transition: transform 0.3s ease;

      &--just-unlocked {
        animation: pdp-step-celebrate 1.4s cubic-bezier(0.34, 1.56, 0.64, 1);

        .pdp-discount-incentive__stepper-node {
          animation: pdp-node-pop 1.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .pdp-discount-incentive__stepper-discount {
          animation: pdp-discount-bounce 1.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      }

      &--just-unlocked-step-2 {
        .pdp-discount-incentive__stepper-node {
          animation: pdp-node-pop-grand 1.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .pdp-discount-incentive__stepper-discount {
          animation: pdp-discount-bounce-grand 1.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      }

      &--just-unlocked-step-3 {
        .pdp-discount-incentive__stepper-node {
          animation: pdp-node-pop-max 1.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .pdp-discount-incentive__stepper-discount {
          animation: pdp-discount-bounce-max 1.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      }
    }

    &-node {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: #ffffff;
      border: 2px solid #d1d5db;
      transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;

      .pdp-discount-incentive__stepper-step--active & {
        border-color: #10b981;
        background-color: #10b981;
        box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.25);
      }

      .pdp-discount-incentive__stepper-step--current & {
        border: 2.5px solid #10b981;
        background-color: #10b981;
        box-shadow: 0 0 0 5px rgba(16, 185, 129, 0.35);
      }
    }

    &-check {
      width: 11px;
      height: 11px;
      animation: pdp-check-appear 0.35s ease forwards;
    }

    &-label {
      margin-top: 6px;
      display: flex;
      flex-direction: column;
      align-items: center;
      line-height: 1.25;
    }

    &-count {
      font-size: 12px;
      font-weight: 600;
      color: #374151;
    }

    &-discount {
      font-size: 12px;
      font-weight: 700;
      color: #ef4444;
      margin-top: 1px;
      transition: transform 0.3s ease;
    }
  }
}

@keyframes pdp-node-pop {
  0% {
    transform: scale(0.8);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.8);
  }
  50% {
    transform: scale(1.4);
    box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 5px rgba(16, 185, 129, 0.35);
  }
}

@keyframes pdp-node-pop-grand {
  0% {
    transform: scale(0.7);
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.9);
  }
  45% {
    transform: scale(1.6);
    box-shadow: 0 0 0 14px rgba(245, 158, 11, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 5px rgba(16, 185, 129, 0.35);
  }
}

@keyframes pdp-node-pop-max {
  0% {
    transform: scale(0.6);
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 1);
  }
  40% {
    transform: scale(1.8);
    box-shadow: 0 0 0 20px rgba(239, 68, 68, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0.45);
  }
}

@keyframes pdp-discount-bounce {
  0% {
    transform: scale(1);
  }
  30% {
    transform: scale(1.35) translateY(-3px);
    color: #059669;
  }
  60% {
    transform: scale(1.15) translateY(-1px);
    color: #10b981;
  }
  100% {
    transform: scale(1) translateY(0);
  }
}

@keyframes pdp-discount-bounce-grand {
  0% {
    transform: scale(1);
  }
  30% {
    transform: scale(1.5) translateY(-5px);
    color: #d97706;
  }
  60% {
    transform: scale(1.2) translateY(-2px);
    color: #10b981;
  }
  100% {
    transform: scale(1) translateY(0);
  }
}

@keyframes pdp-discount-bounce-max {
  0% {
    transform: scale(1);
  }
  25% {
    transform: scale(1.7) translateY(-7px);
    color: #ef4444;
  }
  55% {
    transform: scale(1.3) translateY(-3px);
    color: #f59e0b;
  }
  100% {
    transform: scale(1) translateY(0);
  }
}

@keyframes pdp-step-celebrate {
  0% {
    transform: translateY(0);
  }
  35% {
    transform: translateY(-5px);
  }
  70% {
    transform: translateY(1px);
  }
  100% {
    transform: translateY(0);
  }
}

@keyframes pdp-check-appear {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  60% {
    opacity: 1;
    transform: scale(1.25);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes pdp-shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 150%;
  }
}
</style>
